import {useStaticQuery, graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import {Modal, DialogBody, DialogContent, CloseWrapper} from '@/components/modal'
import type {UnifiedSignupModalQuery} from '@/graphql-types'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import type {Args} from '@/hooks/use-signup'
import mq from '@/utils/media-query'

import {LeftColumn} from './components/left-column'
import {SliceZone} from './slice-zone'

type UnifiedSignupModalProps = {
    signupProps: Args
    isOpen?: boolean
    onDismiss: () => void
}

export const UnifiedSignupModal = ({signupProps, isOpen, onDismiss}: UnifiedSignupModalProps) => {
    const data = useStaticQuery<UnifiedSignupModalQuery>(query)
    const [content] = useLocalizedContent(data.allPrismicUnifiedSignupModal.nodes)

    return (
        <StyledModal isOpen={isOpen} onDismiss={onDismiss}>
            <LeftColumn signupProps={signupProps} content={content} />
            <RightColumnContainer>
                <SliceZone slices={content.data.body} />
            </RightColumnContainer>
        </StyledModal>
    )
}

const StyledModal = styled(Modal)`
    ${CloseWrapper} {
        width: auto;
        z-index: 1;
        padding: 0;
        position: absolute;
        top: 8px;
        right: 8px;
        ${mq.min('md')} {
            top: 24px;
            right: 24px;
        }
    }
    ${DialogContent} {
        position: relative;
        ${mq.min('md')} {
            max-width: 1080px;
        }
    }
    ${DialogBody} {
        padding: 0;
        ${mq.min('md')} {
            height: 720px;
            grid-template-columns: 1fr 1fr;
        }
    }
`

const RightColumnContainer = styled.div`
    overflow: hidden;
    background-color: ${({theme}) => theme.colors.background.muted};
    border-top-right-radius: ${({theme}) => theme.radii.arc8};
    border-bottom-right-radius: ${({theme}) => theme.radii.arc8};
    display: none;
    ${mq.min('md')} {
        display: block;
    }
`

export const query = graphql`
    query UnifiedSignupModal {
        allPrismicUnifiedSignupModal {
            nodes {
                lang
                data {
                    title
                    subtitle
                    placeholder: email_input_placeholder
                    ariaLabel: aria_label
                    submitText: submit_text
                    errorText: email_input_error_text
                    form_production {
                        formId
                        consents {
                            type
                            communicationConsents {
                                id
                                required
                            }
                        }
                    }
                    form_staging {
                        formId
                        consents {
                            type
                            communicationConsents {
                                id
                                required
                            }
                        }
                    }
                    highlightItems: highlight_items {
                        text
                        image {
                            dimensions {
                                height
                                width
                            }
                            alt
                            copyright
                            url
                            gatsbyImageData(width: 32, layout: FIXED)
                        }
                    }
                    body {
                        ...UnifiedSignupModalCroppedImage
                        ...UnifiedSignupModalSocialProof
                    }
                }
            }
        }
    }

    fragment UnifiedSignupModalCroppedImage on PrismicUnifiedSignupModalDataBodyCroppedImage {
        __typename
        primary {
            text
            image {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
                gatsbyImageData(width: 420)
            }
        }
    }

    fragment UnifiedSignupModalSocialProof on PrismicUnifiedSignupModalDataBodySocialProof {
        __typename
        items {
            quote
            full_name
            person_byline
            image {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
                gatsbyImageData(width: 350)
            }
        }
    }
`
